import { useCallback, useState } from "react";
import { toast } from "sonner";
import { createFyCycleOption } from "../api/fyCycleOptions";
import { useFyCycleOptions } from "./useFyCycleOptions";

/**
 * Manages the FY cycle selection field shared by the create-session dialogs
 * and the session edit dialog.
 *
 * The hook splits user intent (combobox state, "set as default?" choice) from
 * the DB write. `handleChange` is synchronous and only updates local state;
 * if the typed name is new, it opens the "set as default?" prompt so the user
 * can answer at the natural moment, but no row is created yet. The actual
 * `createFyCycleOption` call happens in `resolveFyCycleId` at commit time
 * (Save / Create), with the captured isDefault flag passed through. This
 * keeps creation atomic with the user's commit and lets failures bubble out
 * of the submit handler instead of being silently toasted on combobox change.
 */
export function useFyCycleField() {
  const [fyCycle, setFyCycle] = useState("");
  const [setAsDefaultOnSave, setSetAsDefaultOnSave] = useState(false);
  const [pendingDefaultName, setPendingDefaultName] = useState<string | null>(
    null,
  );
  const { options: fyCycleOptionObjects, refetch: refetchFyCycleOptions } =
    useFyCycleOptions();
  const fyCycleOptionNames = fyCycleOptionObjects.map((o) => o.name);

  /** Call when the create-session dialog opens to pre-select the workspace default. */
  const initWithDefault = useCallback(async () => {
    const freshOptions = await refetchFyCycleOptions();
    const optionsToUse = freshOptions ?? fyCycleOptionObjects;
    const defaultOption = optionsToUse.find((o) => o.isDefault);
    setFyCycle(defaultOption?.name ?? "");
    setSetAsDefaultOnSave(false);
    setPendingDefaultName(null);
  }, [fyCycleOptionObjects, refetchFyCycleOptions]);

  /**
   * Combobox onChange handler. Pure state update — does NOT hit the DB.
   * If the value is a new name, opens the "set as default?" prompt so the
   * user can answer immediately; the answer is held in local state and
   * applied during `resolveFyCycleId`.
   */
  const handleChange = useCallback(
    (value: string) => {
      setFyCycle(value);
      setSetAsDefaultOnSave(false);
      if (!value) {
        setPendingDefaultName(null);
        return;
      }
      const existing = fyCycleOptionObjects.find((o) => o.name === value);
      setPendingDefaultName(existing ? null : value);
    },
    [fyCycleOptionObjects],
  );

  /** User confirmed "set as default" in the AlertDialog. */
  const confirmSetDefault = useCallback(() => {
    setSetAsDefaultOnSave(true);
    setPendingDefaultName(null);
  }, []);

  /** User dismissed the "set as default" dialog. */
  const dismissSetDefault = useCallback(() => {
    setPendingDefaultName(null);
  }, []);

  /**
   * Resolve the current `fyCycle` string to a concrete id at commit time.
   * Returns null when the field is empty (the user cleared it). For an
   * existing option, returns its id without a DB call. For a new name,
   * creates the option (with the captured `setAsDefaultOnSave` flag) and
   * returns the new id. Throws on create failure after toasting.
   */
  const resolveFyCycleId = useCallback(async (): Promise<string | null> => {
    if (!fyCycle) return null;
    const existing = fyCycleOptionObjects.find((o) => o.name === fyCycle);
    if (existing) return existing.id;
    try {
      const newOption = await createFyCycleOption(fyCycle, setAsDefaultOnSave);
      await refetchFyCycleOptions();
      return newOption.id;
    } catch (error) {
      console.error("Failed to create FY cycle option:", error);
      toast.error("Failed to create FY cycle option");
      throw error;
    }
  }, [
    fyCycle,
    fyCycleOptionObjects,
    setAsDefaultOnSave,
    refetchFyCycleOptions,
  ]);

  const reset = useCallback(() => {
    setFyCycle("");
    setSetAsDefaultOnSave(false);
    setPendingDefaultName(null);
  }, []);

  return {
    fyCycle,
    fyCycleOptionNames,
    pendingDefaultName,
    initWithDefault,
    handleChange,
    setFyCycle,
    confirmSetDefault,
    dismissSetDefault,
    resolveFyCycleId,
    reset,
  };
}
